"use client";

import { emitUsageEvent } from "../lib/umami";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Code2,
  Copy,
  Download,
  Grid2X2,
  Heart,
  List,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import styles from "./PedModelBrowser.module.css";

export interface PedModel {
  id: string;
  name: string;
  categoryId: string;
  category: string;
  hash: string;
  decimalHash: string;
  props: string;
  components: string;
  previewUrl: string | null;
  imageWidth: number;
  imageHeight: number;
}

interface PedModelBrowserProps {
  models: PedModel[];
  sourceUrl: string;
  retrievedAt: string;
  downloadUrl: string;
  pageSize?: number;
}

type ViewMode = "grid" | "list";
type SortMode = "name-asc" | "name-desc" | "category";
type CodeLanguage = "lua" | "javascript";
const FAVORITES_KEY = "fivemdocs:favorite-ped-models";
const FAVORITES_EVENT = "fivemdocs:ped-favorites-changed";
const MODEL_EVENT = "fivemdocs:ped-model-link-changed";

// Browser-only state uses server snapshots so saved preferences and deep links
// hydrate without changing the HTML React expects from the server.
function subscribeFavorites(callback: () => void) {
  window.addEventListener("storage", callback);
  window.addEventListener(FAVORITES_EVENT, callback);
  return () => {
    window.removeEventListener("storage", callback);
    window.removeEventListener(FAVORITES_EVENT, callback);
  };
}

function readFavorites() {
  try {
    return window.localStorage.getItem(FAVORITES_KEY) ?? "[]";
  } catch {
    return "[]";
  }
}

function serverFavorites() {
  return "[]";
}

function subscribeModelLink(callback: () => void) {
  window.addEventListener("popstate", callback);
  window.addEventListener(MODEL_EVENT, callback);
  return () => {
    window.removeEventListener("popstate", callback);
    window.removeEventListener(MODEL_EVENT, callback);
  };
}

function readModelLink() {
  return new URLSearchParams(window.location.search).get("model") ?? "";
}

function serverModelLink() {
  return "";
}

export function buildPedSnippet(name: string, language: CodeLanguage): string {
  const modelName = JSON.stringify(name);

  if (language === "lua") {
    return [
      "-- Client-side Lua example; adjust the spawn coordinates.",
      "local model = joaat(" + modelName + ")",
      'if not IsModelInCdimage(model) or not IsModelValid(model) then',
      '    error("Invalid ped model")',
      "end",
      "",
      "RequestModel(model)",
      "local deadline = GetGameTimer() + 5000",
      "while not HasModelLoaded(model) do",
      "    if GetGameTimer() >= deadline then",
      '        error("Timed out loading ped model")',
      "    end",
      "    Wait(0)",
      "end",
      "",
      "local ped = CreatePed(4, model, 215.76, -810.12, 30.73, 158.0, true, false)",
      "SetEntityAsMissionEntity(ped, true, true)",
      "SetModelAsNoLongerNeeded(model)",
    ].join("\n");
  }

  return [
    "// Client-side JavaScript example; adjust the spawn coordinates.",
    "async function spawnPed() {",
    "  const model = GetHashKey(" + modelName + ");",
    "  if (!IsModelInCdimage(model) || !IsModelValid(model)) {",
    '    throw new Error("Invalid ped model");',
    "  }",
    "",
    "  RequestModel(model);",
    "  const deadline = GetGameTimer() + 5000;",
    "  while (!HasModelLoaded(model)) {",
    "    if (GetGameTimer() >= deadline) {",
    '      throw new Error("Timed out loading ped model");',
    "    }",
    "    await Delay(0);",
    "  }",
    "",
    "  const ped = CreatePed(4, model, 215.76, -810.12, 30.73, 158.0, true, false);",
    "  SetEntityAsMissionEntity(ped, true, true);",
    "  SetModelAsNoLongerNeeded(model);",
    "}",
    "",
    "spawnPed().catch(console.error);",
  ].join("\n");
}

export default function PedModelBrowser({
  models,
  sourceUrl,
  retrievedAt,
  downloadUrl,
  pageSize = 24,
}: PedModelBrowserProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState<SortMode>("name-asc");
  const [view, setView] = useState<ViewMode>("grid");
  const [page, setPage] = useState(1);
  const [fallbackFavorites, setFallbackFavorites] = useState<string[] | null>(null);
  const [language, setLanguage] = useState<CodeLanguage>("lua");
  const [feedback, setFeedback] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const categories = useMemo(() => {
    const counts = new Map<string, { title: string; count: number }>();
    for (const model of models) {
      const existing = counts.get(model.categoryId);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(model.categoryId, { title: model.category, count: 1 });
      }
    }
    return Array.from(counts, ([id, value]) => ({ id, ...value }));
  }, [models]);

  const validNames = useMemo(
    () => new Set(models.map((model) => model.name)),
    [models]
  );

  const favoritesSnapshot = useSyncExternalStore(
    subscribeFavorites,
    readFavorites,
    serverFavorites
  );
  const selectedName = useSyncExternalStore(
    subscribeModelLink,
    readModelLink,
    serverModelLink
  );
  const favorites = useMemo(() => {
    if (fallbackFavorites) return fallbackFavorites;
    try {
      const stored: unknown = JSON.parse(favoritesSnapshot);
      return Array.isArray(stored)
        ? stored.filter(
            (name): name is string =>
              typeof name === "string" && validNames.has(name)
          )
        : [];
    } catch {
      return [];
    }
  }, [favoritesSnapshot, fallbackFavorites, validNames]);

  useEffect(() => {
    function focusSearch(event: KeyboardEvent) {
      if (
        event.key !== "/" ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        dialogRef.current?.open
      ) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest("input, textarea, select, [contenteditable]"))
      ) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    }

    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  const favoriteNames = useMemo(() => new Set(favorites), [favorites]);
  const filtered = useMemo(() => {
    const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const matches = models.filter((model) => {
      if (category === "favorites" && !favoriteNames.has(model.name)) return false;
      if (category !== "all" && category !== "favorites" && model.categoryId !== category) {
        return false;
      }

      const searchable = [
        model.name,
        model.hash,
        model.decimalHash,
        model.category,
      ].join(" ").toLowerCase();
      return terms.every((term) => searchable.includes(term));
    });

    return matches.sort((a, b) => {
      if (sort === "category") {
        const byCategory = a.category.localeCompare(b.category);
        if (byCategory !== 0) return byCategory;
      }
      const byName = a.name.localeCompare(b.name);
      return sort === "name-desc" ? -byName : byName;
    });
  }, [models, category, favoriteNames, query, sort]);

  const safeSize = Math.max(1, Math.floor(pageSize) || 24);
  const pageCount = Math.max(1, Math.ceil(filtered.length / safeSize));
  const currentPage = Math.min(page, pageCount);
  const firstResult = (currentPage - 1) * safeSize;
  const visible = filtered.slice(firstResult, firstResult + safeSize);
  const selected = models.find((model) => model.name === selectedName) ?? null;
  const selectedIndex = selected
    ? filtered.findIndex((model) => model.name === selected.name)
    : -1;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (selected) {
      if (!dialog.open && typeof dialog.showModal === "function") {
        dialog.showModal();
        closeRef.current?.focus();
      }
    } else if (dialog.open) {
      dialog.close();
    }
  }, [selected]);

  function updateSelectedUrl(name: string | null) {
    const url = new URL(window.location.href);
    if (name) url.searchParams.set("model", name);
    else url.searchParams.delete("model");
    window.history.replaceState(window.history.state, "", url.toString());
    window.dispatchEvent(new Event(MODEL_EVENT));
  }

  function openDetails(model: PedModel) {
    setLanguage("lua");
    updateSelectedUrl(model.name);
  }

  function closeDetails() {
    dialogRef.current?.close();
  }

  function handleDialogClose() {
    updateSelectedUrl(null);
  }

  function selectCategory(next: string) {
    setCategory(next);
    setPage(1);
  }

  function toggleFavorite(name: string) {
    const next = favorites.includes(name)
      ? favorites.filter((value) => value !== name)
      : [...favorites, name];

    try {
      window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      setFallbackFavorites(null);
      window.dispatchEvent(new Event(FAVORITES_EVENT));
    } catch {
      // Browsers without storage can still use favorites for this visit.
      setFallbackFavorites(next);
    }
  }

  async function copyValue(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      emitUsageEvent("example_copy");
      setFeedback(label + " copied to clipboard");
    } catch {
      setFeedback("Clipboard unavailable. Select the displayed text to copy it.");
    }
  }

  function clearFilters() {
    setQuery("");
    setCategory("all");
    setPage(1);
    searchRef.current?.focus();
  }

  const hasFilters = query.trim() !== "" || category !== "all";
  const snippet = selected ? buildPedSnippet(selected.name, language) : "";

  return (
    <section className={styles.explorer} aria-label="Ped model explorer">
      <header className={styles.intro}>
        <div>
          <span className={styles.eyebrow}>Visual model browser</span>
          <h3>Find your next ped</h3>
          <p>
            Preview a character, copy its model name or hash, and get a ready-to-edit
            spawn example. No server connection required.
          </p>
        </div>
        <a className={styles.download} href={downloadUrl} download>
          <Download size={16} aria-hidden="true" />
          Download JSON
        </a>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={19} aria-hidden="true" />
          <label htmlFor="ped-model-search" className={styles.srOnly}>
            Search ped models by name, hex hash, decimal hash, or category
          </label>
          <input
            id="ped-model-search"
            ref={searchRef}
            type="search"
            autoComplete="off"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search model, hash, or category…"
          />
          {query ? (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => {
                setQuery("");
                setPage(1);
                searchRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <X size={17} aria-hidden="true" />
            </button>
          ) : (
            <kbd className={styles.shortcut} aria-hidden="true">/</kbd>
          )}
        </div>
        <label className={styles.sortLabel}>
          <span>Sort</span>
          <select
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as SortMode);
              setPage(1);
            }}
          >
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
            <option value="category">Category</option>
          </select>
        </label>
        <div className={styles.viewSwitcher} role="group" aria-label="Display mode">
          <button
            type="button"
            onClick={() => setView("grid")}
            className={view === "grid" ? styles.activeView : undefined}
            aria-pressed={view === "grid"}
            aria-label="Grid view"
            title="Grid view"
          >
            <Grid2X2 size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            className={view === "list" ? styles.activeView : undefined}
            aria-pressed={view === "list"}
            aria-label="List view"
            title="List view"
          >
            <List size={18} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className={styles.filters} role="group" aria-label="Filter by ped category">
        <button
          type="button"
          aria-pressed={category === "all"}
          className={category === "all" ? styles.activeFilter : undefined}
          onClick={() => selectCategory("all")}
        >
          All models <span>{models.length}</span>
        </button>
        <button
          type="button"
          aria-pressed={category === "favorites"}
          className={category === "favorites" ? styles.activeFilter : undefined}
          onClick={() => selectCategory("favorites")}
        >
          <Heart size={14} aria-hidden="true" />
          Favorites <span>{favorites.length}</span>
        </button>
        {categories.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={category === item.id}
            className={category === item.id ? styles.activeFilter : undefined}
            onClick={() => selectCategory(item.id)}
          >
            {item.title} <span>{item.count}</span>
          </button>
        ))}
      </div>

      <div className={styles.resultsHeading}>
        <p aria-live="polite" aria-atomic="true">
          <strong>{filtered.length}</strong> {filtered.length === 1 ? "model" : "models"}
          {hasFilters ? " found" : " available"}
          <span className={styles.resultRange}>
            {filtered.length > 0 &&
              " · Showing " + (firstResult + 1) + "–" + (firstResult + visible.length)}
          </span>
        </p>
        {hasFilters && (
          <button type="button" className={styles.reset} onClick={clearFilters}>
            <X size={14} aria-hidden="true" /> Clear filters
          </button>
        )}
      </div>

      {visible.length > 0 ? (
        <ul className={view === "grid" ? styles.grid : styles.list}>
          {visible.map((model, index) => {
            const isFavorite = favoriteNames.has(model.name);
            return (
              <li key={model.id} className={styles.card}>
                <button
                  type="button"
                  className={styles.previewButton}
                  onClick={() => openDetails(model)}
                  aria-label={"View details for " + model.name}
                >
                  {model.previewUrl ? (
                    // Source images are local, already optimized WebP assets.
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={model.previewUrl}
                      alt={"Preview of " + model.name}
                      width={model.imageWidth}
                      height={model.imageHeight}
                      loading={index < 6 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  ) : (
                    <span className={styles.noPreview}>No preview available</span>
                  )}
                </button>
                <div className={styles.cardBody}>
                  <span className={styles.categoryTag}>{model.category}</span>
                  <button
                    type="button"
                    className={styles.modelName}
                    onClick={() => openDetails(model)}
                  >
                    <code>{model.name}</code>
                  </button>
                  <span className={styles.hashLine}>
                    <span>Hash</span> <code>{model.hash}</code>
                  </span>
                  <div className={styles.cardActions}>
                    <button
                      type="button"
                      className={styles.copyModel}
                      onClick={() => copyValue(model.name, "Model name")}
                      aria-label={"Copy model " + model.name}
                      title="Copy model name"
                    >
                      <Copy size={15} aria-hidden="true" />
                      <span>Copy model</span>
                    </button>
                    <button
                      type="button"
                      className={styles.favoriteButton}
                      aria-label={
                        (isFavorite ? "Remove " : "Add ") +
                        model.name +
                        (isFavorite ? " from favorites" : " to favorites")
                      }
                      aria-pressed={isFavorite}
                      onClick={() => toggleFavorite(model.name)}
                      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Heart
                        size={18}
                        fill={isFavorite ? "currentColor" : "none"}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={styles.emptyState}>
          <Search size={26} aria-hidden="true" />
          <h3>
            {category === "favorites" && !query ? "No favorites yet" : "No matching models"}
          </h3>
          <p>
            {category === "favorites" && !query
              ? "Save models with the heart button to find them here later."
              : "Try a different model name, hash, or category."}
          </p>
          <button type="button" onClick={clearFilters}>View all models</button>
        </div>
      )}

      {pageCount > 1 && (
        <nav className={styles.pagination} aria-label="Model results pagination">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} aria-hidden="true" /> Previous
          </button>
          <label>
            Page
            <select
              aria-label="Page number"
              value={currentPage}
              onChange={(event) => setPage(Number(event.target.value))}
            >
              {Array.from({ length: pageCount }, (_, index) => (
                <option key={index + 1} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </select>
            of {pageCount}
          </label>
          <button
            type="button"
            disabled={currentPage === pageCount}
            onClick={() => setPage(currentPage + 1)}
            aria-label="Next page"
          >
            Next <ChevronRight size={18} aria-hidden="true" />
          </button>
        </nav>
      )}

      <footer className={styles.source}>
        Model data and previews: <a href={sourceUrl}>official FiveM reference</a>.
        Snapshot <time dateTime={retrievedAt}>{retrievedAt}</time>.
        Model availability can vary by game build.
      </footer>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby="ped-detail-title"
        onClose={handleDialogClose}
        onClick={(event) => {
          if (event.target === event.currentTarget) closeDetails();
        }}
      >
        {selected && (
          <>
            <header className={styles.dialogHeader}>
              <div>
                <span className={styles.eyebrow}>{selected.category}</span>
                <h3 id="ped-detail-title"><code>{selected.name}</code></h3>
              </div>
              <button
                ref={closeRef}
                type="button"
                className={styles.closeDialog}
                onClick={closeDetails}
                aria-label="Close model details"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </header>
            <div className={styles.dialogContent}>
              <div className={styles.largePreview}>
                {selected.previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={selected.previewUrl}
                    alt={"Full preview of " + selected.name}
                    width={selected.imageWidth}
                    height={selected.imageHeight}
                  />
                ) : (
                  <p>No preview available</p>
                )}
              </div>
              <div className={styles.modelDetails}>
                <p className={styles.detailHint}>
                  Copy the exact identifier into your resource or use the generated
                  spawn code below.
                </p>
                <dl>
                  {[
                    { label: "Model name", value: selected.name },
                    { label: "Hash · hexadecimal", value: selected.hash },
                    { label: "Hash · unsigned decimal", value: selected.decimalHash },
                    { label: "Props", value: selected.props || "Unknown" },
                    { label: "Components", value: selected.components || "Unknown" },
                  ].map((item) => (
                    <div key={item.label} className={styles.detailRow}>
                      <dt>{item.label}</dt>
                      <dd>
                        <code>{item.value}</code>
                        <button
                          type="button"
                          onClick={() => copyValue(item.value, item.label)}
                          aria-label={"Copy " + item.label.toLowerCase()}
                          title={"Copy " + item.label}
                        >
                          <Copy size={15} aria-hidden="true" />
                        </button>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
            <div className={styles.codePanel}>
              <div className={styles.codeHeading}>
                <div className={styles.codeTitle}>
                  <Code2 size={18} aria-hidden="true" />
                  <strong>Spawn this ped</strong>
                </div>
                <div className={styles.languageSwitch} role="group" aria-label="Code language">
                  <button
                    type="button"
                    aria-pressed={language === "lua"}
                    onClick={() => setLanguage("lua")}
                  >
                    Lua
                  </button>
                  <button
                    type="button"
                    aria-pressed={language === "javascript"}
                    onClick={() => setLanguage("javascript")}
                  >
                    JavaScript
                  </button>
                </div>
              </div>
              <pre><code>{snippet}</code></pre>
              <div className={styles.codeFooter}>
                <span>Client-side example · change the example coordinates</span>
                <button
                  type="button"
                  className={styles.copySnippet}
                  onClick={() => copyValue(snippet, "Spawn code")}
                >
                  <Copy size={15} aria-hidden="true" /> Copy code
                </button>
              </div>
            </div>
            <div className={styles.dialogFooter}>
              <button
                type="button"
                onClick={() => toggleFavorite(selected.name)}
                aria-pressed={favoriteNames.has(selected.name)}
              >
                <Heart
                  size={17}
                  fill={favoriteNames.has(selected.name) ? "currentColor" : "none"}
                  aria-hidden="true"
                />
                {favoriteNames.has(selected.name) ? "Saved to favorites" : "Save to favorites"}
              </button>
              <button
                type="button"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set("model", selected.name);
                  copyValue(url.toString(), "Model link");
                }}
              >
                <Copy size={16} aria-hidden="true" /> Copy link
              </button>
              {selectedIndex >= 0 && filtered.length > 1 && (
                <div className={styles.detailNavigation}>
                  <button
                    type="button"
                    disabled={selectedIndex === 0}
                    aria-label="Previous model"
                    onClick={() => openDetails(filtered[selectedIndex - 1])}
                  >
                    <ChevronLeft size={17} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    disabled={selectedIndex === filtered.length - 1}
                    aria-label="Next model"
                    onClick={() => openDetails(filtered[selectedIndex + 1])}
                  >
                    <ChevronRight size={17} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </dialog>

      <div className={styles.feedback} role="status" aria-live="polite">
        {feedback && (
          <>
            {feedback.includes("copied") ? (
              <Check size={16} aria-hidden="true" />
            ) : (
              <X size={16} aria-hidden="true" />
            )}
            {feedback}
          </>
        )}
      </div>
    </section>
  );
}
